import { raporBaskaAile, raporDosyaBul, type RaporTuru } from "@oasis/domain";
import { cevir } from "@/lib/dil";
import { ekRejectNotuMu } from "@/lib/ek-reject";
import { AG_HATASI, dosyaTuru, sirali, type DosyaBenzeri, type Ton } from "@/lib/mrb-toplu";

/**
 * D-065: "Upload Disposition reports" ve "Upload Rework reports" pencerelerinin saf mantığı (söküm penceresindeki `sokum-toplu.ts`'in
 * kopyası; o dosya canlı ekranındır, bilerek ortaklaştırılmadı). Tek pencere bileşeni (RaporTopluYukleme.tsx) iki türü de çizer;
 * tür her fonksiyona parametre olarak gelir. Eşleme, satır durumları, sunucu yanıtının satır sonucuna çevrilmesi, sıradaki satırın
 * seçimi ve gösterilecek metinler burada. JSX yok; React ve oturum katmanını yüklemez.
 * Söküm penceresinden bilinçli farklar: tür ve riser dosya adından `raporDosyaBul` ile okunur; öteki pencerenin dosyası satır olur
 * ama GÖNDERİLMEZ; riser girdiyi beklerken tutulmaz; rework girdisi riserin disposition girdisi yokken kabul edilmez ("onkosul").
 */

export type { RaporTuru };

/**
 * bekliyor: gönderilecek · yukleniyor: istek sürüyor · tamam: sunucu yanıtladı (`sonuc` dolu) ·
 * rn_gerekli: riser okunamadı, satırda seçilir · hata: sunucu ya da ağ hatası, ya da öteki pencerenin ya da başka bir belge
 * ailesinin dosyası (gönderilmez).
 */
export type SatirDurumu = "bekliyor" | "yukleniyor" | "tamam" | "rn_gerekli" | "hata";

/** Disposition girdisi okunurken MRB bağının sonucu (sunucudaki `DispositionMrbSonucu`). */
export type DispositionMrbSonucu = { durum: "baglandi"; sn: string } | { durum: "mrb_yok"; sn: string } | { durum: "not"; not: string };

/** İki ucun ortak alanları (sözleşme U-059). */
interface OrtakYanit {
  sonuc: "yuklendi" | "girdi_baglandi" | "degismedi" | "pdf_gerekli";
  rn: string;
  /** Rapor kaydı; kaydı olmayan riserde null. */
  id: string | null;
  /** Yalnız PDF: rapor dönüştürücü sırasına girdi. */
  kuyrugaGirdi?: boolean;
  siraNo?: number | null;
  /** Yalnız PDF, aynı PDF yeniden geldi: rapor şu anda dönüştürücüdedir; kayda ve işe dokunulmadı (`siraNo` null). */
  donusturuluyor?: boolean;
  /** girdi_baglandi: kaydın dönüşüm işi düştü (sıradaydı, dönüştürülüyordu ya da hata vermişti). */
  siradanCikti?: boolean;
  /** girdi_baglandi: satırda PDF de vardı; kayıttaki PDF kaldı, satırdaki kaydedilmedi. */
  pdfKaydedilmedi?: boolean;
  uyarilar: string[];
}

/** `POST /api/rapor/disposition/toplu` yanıtı. */
export interface DispositionYaniti extends OrtakYanit {
  ozet?: { rn: string; gorunurKomponent: number; kalem: number; celiski: number };
  mrb?: DispositionMrbSonucu;
  /** Bu yüklemenin "not performed" yazdığı önceki saha adımları (adım kodları). */
  atlananAdimlar?: string[];
  teslimAtlandi?: boolean;
}

/** `POST /api/rapor/rework/toplu` yanıtı. */
export interface ReworkYaniti extends OrtakYanit {
  ozet?: { kapanan: number; devir: number; dogrulanmayan: number; ek: number; rota: number; celiski: number };
  /** Yalnız PDF ve riserin disposition girdisi yok: iş sırada, ön koşulunu bekliyor. */
  onKosul?: "disposition";
}

export type TopluYaniti = DispositionYaniti | ReworkYaniti;

export interface SatirHatasi {
  /** Sunucunun İngilizce metni (gösterimde çevrilir) ya da bu modülün metni. */
  metin: string;
  /**
   * "onkosul": rework girdisi disposition girdisinden önce geldi · "tur": dosya öteki pencerenin ya da başka bir belge ailesinindir,
   * gönderilmedi · "oturum": oturum bitti (401), tur durdu.
   */
  kaynak: "rn" | "excel" | "onkosul" | "tur" | "oturum" | null;
  /** Ağ hatası, sunucu arızası (5xx), biten oturum (401), zaman aşımı (408) ya da hız sınırı (429): aynı satır olduğu gibi yeniden denenebilir. */
  tekrarlanabilir: boolean;
}

export interface RaporSatiri<D extends DosyaBenzeri = File> {
  id: number;
  pdf: D | null;
  excel: D | null;
  /** Dosya adından okunan riser (eşleme bununla yapılır). */
  rn: string | null;
  /** Kullanıcının satırda seçtiği riser; sunucuya yalnız bu gider (dosya adındakini sunucu kendisi okur). */
  secilenRn: string | null;
  durum: SatirDurumu;
  sonuc: TopluYaniti | null;
  hata: SatirHatasi | null;
}

/** Pencerenin gönderdiği uç. */
export const TOPLU_UCU: Record<RaporTuru, string> = { disposition: "/rapor/disposition/toplu", rework: "/rapor/rework/toplu" };

/** Dosya öteki pencerenin türündeyse o pencereyi söyleyen cümle (satır gönderilmez). */
const YANLIS_PENCERE: Record<RaporTuru, string> = {
  disposition: "This looks like a Rework report. Upload it with Upload Rework reports.",
  rework: "This looks like a Disposition report. Upload it with Upload Disposition reports.",
};

/** Dosyanın adı başka bir belge ailesini söylüyor (söküm kaydı, MRB): satır gönderilmez. Sunucu aynı cümleyle reddeder. */
export const BASKA_AILE = "The file name says this is a disassembly record or an MRB. It is not uploaded from this window.";

/** Oturum bitti (401): satır ve kalan bekleyenler gönderilmedi. */
const OTURUM_BITTI = "Your session has ended, so this row and the waiting rows were not sent. Sign in again in another tab, then retry this row and press Upload.";

/** Satırın riseri: satırda seçilen, yoksa dosya adındaki. */
export const etkinRn = (s: Pick<RaporSatiri<DosyaBenzeri>, "rn" | "secilenRn">): string | null => s.secilenRn ?? s.rn;

function yeniSatir<D extends DosyaBenzeri>(id: number, pdf: D | null, excel: D | null, rn: string | null): RaporSatiri<D> {
  return { id, pdf, excel, rn, secilenRn: null, durum: rn ? "bekliyor" : "rn_gerekli", sonuc: null, hata: null };
}

/** PDF'ini bekleyen tek başına excel satırı: henüz gönderilmemiş ya da sunucudan "needs its PDF" almış. */
const pdfBekleyenExcel = (s: RaporSatiri<DosyaBenzeri>) => !s.pdf && Boolean(s.excel) && (s.durum === "bekliyor" || (s.durum === "tamam" && s.sonuc?.sonuc === "pdf_gerekli"));

/**
 * Seçilen ya da sürüklenen dosyaları satırlara ekler. Önce PDF'ler, sonra excel'ler işlenir ki aynı seçimde sıra fark etmesin.
 * - Dosya adı öteki pencerenin türünü ya da başka bir belge ailesini (söküm kaydı, MRB: `raporBaskaAile`) söylüyorsa dosya kendi
 *   satırında "error" olur ve gönderilmez; hiçbir satırla eşlenmez. Türü okunamayan başka dosya (ör. elle adlandırılmış
 *   "R047.pdf") bu pencerenin sayılır.
 * - PDF: aynı riserde PDF'ini bekleyen tek başına excel satırı varsa ona girer ("needs its PDF" almış satır yeniden bekler);
 *   yoksa yeni satır. Aynı riserin ikinci PDF'i ayrı satır olur ve sunucudan "not changed" alır.
 * - Excel: aynı riserde excel'i olmayan, henüz gönderilmemiş PDF satırına girer; yoksa yeni satır. Tek başına excel de gönderilir.
 * Riser okunamazsa satır "rn_gerekli" olur. PDF ve .xlsx dışındaki dosyalar eklenmez, adları `atlanan`da döner.
 */
export function dosyalariEkle<D extends DosyaBenzeri>(tur: RaporTuru, satirlar: readonly RaporSatiri<D>[], dosyalar: readonly D[]): { satirlar: RaporSatiri<D>[]; atlanan: string[] } {
  const sonuc = satirlar.map((s) => ({ ...s }));
  let id = sonuc.reduce((m, s) => Math.max(m, s.id), 0);
  const atlanan = dosyalar.filter((f) => !dosyaTuru(f.name)).map((f) => f.name);
  /** Öteki pencerenin ya da başka bir belge ailesinin dosyası: satırı açılır, gönderilmez. */
  const yanlisTur = (f: D, okunan: RaporTuru | null, rn: string | null): boolean => {
    const metin = okunan && okunan !== tur ? YANLIS_PENCERE[tur] : raporBaskaAile(f.name) ? BASKA_AILE : null;
    if (!metin) return false;
    const pdf = dosyaTuru(f.name) === "pdf";
    sonuc.push({ ...yeniSatir(++id, pdf ? f : null, pdf ? null : f, rn), durum: "hata", hata: { metin, kaynak: "tur", tekrarlanabilir: false } });
    return true;
  };

  for (const f of dosyalar.filter((x) => dosyaTuru(x.name) === "pdf")) {
    const { tur: okunan, rn } = raporDosyaBul(f.name);
    if (yanlisTur(f, okunan, rn)) continue;
    const bekleyen = rn ? sonuc.find((s) => pdfBekleyenExcel(s) && etkinRn(s) === rn) : undefined;
    if (bekleyen) {
      bekleyen.pdf = f;
      bekleyen.durum = "bekliyor";
      bekleyen.sonuc = null;
      continue;
    }
    sonuc.push(yeniSatir(++id, f, null, rn));
  }

  for (const f of dosyalar.filter((x) => dosyaTuru(x.name) === "excel")) {
    const { tur: okunan, rn } = raporDosyaBul(f.name);
    if (yanlisTur(f, okunan, rn)) continue;
    const pdfSatiri = rn ? sonuc.find((s) => s.durum === "bekliyor" && s.pdf && !s.excel && etkinRn(s) === rn) : undefined;
    if (pdfSatiri) {
      pdfSatiri.excel = f;
      continue;
    }
    sonuc.push(yeniSatir(++id, null, f, rn));
  }
  return { satirlar: sonuc, atlanan };
}

/**
 * Riseri okunamayan satırda seçilen riser ("Retry"): satır yeniden gönderilmek üzere bekler. Aynı riserin öteki yarısı ayrı
 * satırda gönderilmeyi bekliyorsa bu satıra taşınır, o satır kalkar: satırın excel'i yoksa PDF'ini bekleyen tek başına excel,
 * satırın PDF'i yoksa excel'i olmayan bekleyen PDF. Böylece ikisi tek istekte gider.
 */
export function rnSec<D extends DosyaBenzeri>(satirlar: readonly RaporSatiri<D>[], id: number, rn: string): RaporSatiri<D>[] {
  const hedef = satirlar.find((s) => s.id === id);
  if (!hedef || !rn || (!hedef.pdf && !hedef.excel) || hedef.hata?.kaynak === "tur") return [...satirlar];
  const oteki = satirlar.find((s) => s.id !== id && etkinRn(s) === rn && (
    (hedef.pdf && !hedef.excel && pdfBekleyenExcel(s))
    || (!hedef.pdf && hedef.excel && s.durum === "bekliyor" && Boolean(s.pdf) && !s.excel)
  ));
  return satirlar
    .filter((s) => s !== oteki)
    .map((s) => (s.id === id ? { ...s, secilenRn: rn, pdf: s.pdf ?? oteki?.pdf ?? null, excel: s.excel ?? oteki?.excel ?? null, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/**
 * Satırı olduğu gibi yeniden kuyruğa koyar ("Retry": ağ hatası, sunucu arızası ya da disposition girdisi yüklendikten sonra
 * "onkosul" reddi). Öteki pencerenin dosyası yeniden denenmez.
 */
export function tekrarDene<D extends DosyaBenzeri>(satirlar: readonly RaporSatiri<D>[], id: number): RaporSatiri<D>[] {
  return satirlar.map((s) => (s.id === id && (s.pdf || s.excel) && s.hata?.kaynak !== "tur" ? { ...s, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/** Excel'i reddedilen satır: excel satırdan çıkar, PDF tek başına yeniden gönderilir ("Retry without excel"). */
export function excelsizDene<D extends DosyaBenzeri>(satirlar: readonly RaporSatiri<D>[], id: number): RaporSatiri<D>[] {
  return satirlar.map((s) => (s.id === id && s.pdf && s.hata?.kaynak !== "tur" ? { ...s, excel: null, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/**
 * Gönderilecek sıradaki satır: listede en üstteki bekleyen (riseri belli, en az bir dosyası olan). `secim` null ise ("Upload" turu)
 * bütün bekleyenler; küme ise yalnız içindeki satırlar (satır düğmesiyle yeniden denenenler). `secimeEkle` MRB penceresiyle ortaktır.
 */
export function siradaki<D extends DosyaBenzeri>(satirlar: readonly RaporSatiri<D>[], secim: ReadonlySet<number> | null = null): RaporSatiri<D> | null {
  return satirlar.find((s) => s.durum === "bekliyor" && (s.pdf || s.excel) && etkinRn(s) && (!secim || secim.has(s.id))) ?? null;
}

/**
 * Satırın isteği: `dosyaYukle(TOPLU_UCU[tur], dosya, alanlar, ekDosyalar, dosyaAlan)`. PDF varsa ana dosya odur ("dosya"), excel
 * yanında gider; tek başına excel "excel" alanında gider. Riser yalnız satırda seçildiyse gönderilir.
 */
export function istekParcalari<D extends DosyaBenzeri>(s: RaporSatiri<D>): { dosya: D; dosyaAlan: "dosya" | "excel"; alanlar: Record<string, string>; ekDosyalar: Record<string, D> } {
  const alanlar: Record<string, string> = s.secilenRn ? { rn: s.secilenRn } : {};
  if (s.pdf) return { dosya: s.pdf, dosyaAlan: "dosya", alanlar, ekDosyalar: s.excel ? { excel: s.excel } : {} };
  if (s.excel) return { dosya: s.excel, dosyaAlan: "excel", alanlar, ekDosyalar: {} };
  throw new Error("Dosya yok: bu satır gönderilmez.");
}

/** Gönderimin ham sonucu: 2xx gövdesi · 4xx/5xx (gövdesiyle) · ağ hatası. */
export type Gonderim =
  | { tur: "yanit"; govde: TopluYaniti }
  | { tur: "hata"; status: number; govde: unknown; mesaj: string }
  | { tur: "ag" };

type SatirSonucu = Pick<RaporSatiri, "durum" | "sonuc" | "hata">;

function hataSonucu(metin: string, kaynak: unknown, tekrarlanabilir: boolean): SatirSonucu {
  const k = kaynak === "rn" || kaynak === "excel" || kaynak === "onkosul" ? kaynak : null;
  return { durum: k === "rn" ? "rn_gerekli" : "hata", sonuc: null, hata: { metin, kaynak: k, tekrarlanabilir } };
}

/**
 * Yanıt turu durdurur mu: oturum bitti (401) ya da bu işleme yetki yok (403). Kalan her satır aynı yanıtı alırdı; dosyaları
 * boşuna gönderilmez, "waiting" kalırlar.
 */
export const turuDurdurur = (g: Gonderim): boolean => g.tur === "hata" && (g.status === 401 || g.status === 403);

/**
 * Sunucu yanıtını satır sonucuna çevirir. Hata 4xx gövdesinde `{hata, hataKaynagi}` ile gelir: "rn" → satırda riser seçimi;
 * "excel" → girdi reddedildi ("Retry without excel"); "onkosul" → riserin disposition girdisi yok ("Retry"); ağ hatası, 5xx, 408
 * ve 429 → "Retry". 401 (oturum bitti) kendi cümlesiyle "Retry" alır; 403 sunucunun yetki cümlesini gösterir, düğmesi yoktur.
 */
export function sonucuIsle(g: Gonderim): SatirSonucu {
  if (g.tur === "ag") return hataSonucu(AG_HATASI, null, true);
  if (g.tur === "hata") {
    if (g.status === 401) return { durum: "hata", sonuc: null, hata: { metin: OTURUM_BITTI, kaynak: "oturum", tekrarlanabilir: true } };
    const govde = (g.govde && typeof g.govde === "object" ? g.govde : {}) as { hata?: unknown; message?: unknown; hataKaynagi?: unknown };
    const metin = typeof govde.hata === "string" ? govde.hata : typeof govde.message === "string" ? govde.message : g.mesaj;
    return hataSonucu(metin, govde.hataKaynagi, g.status >= 500 || g.status === 408 || g.status === 429);
  }
  return { durum: "tamam", sonuc: g.govde, hata: null };
}

export interface SatirGorunumu {
  etiketler: { metin: string; ton: Ton }[];
  aciklama: { metin: string; ton: "ink-2" | "bad" } | null;
  /** Açıklamanın altındaki satırlar: MRB bağı (koyu; bağ kurulamadıysa sunucunun notu sarı), sunucunun uyarıları (sarı), gri notlar. */
  notlar: { metin: string; ton: "ink-2" | "warn" | "ink-3" }[];
  /** RN hücresinde riser seçimi ve "Retry". */
  rnSecimi: boolean;
  dugme: "tekrar" | "excelsiz" | null;
}

/** Türün ekran adları: girdi bağlanan satırın etiketi ve sayaç başlığı. */
export const girdiBaglandiAdi = (tur: RaporTuru): string => (tur === "disposition" ? cevir("disposition input attached") : cevir("rework input attached"));

/** "Now converting" hücresinde rapor: "Disposition report R047" · "Rework report R047". */
export const raporAdi = (tur: RaporTuru, rn: string): string => (tur === "disposition" ? cevir("Disposition report {0}", rn) : cevir("Rework report {0}", rn));

/** Atlanan saha adımının ekran adı (Field list kolon başlıklarıyla aynı metinler); bilinmeyen kod olduğu gibi. */
function adimAdi(kod: string): string {
  switch (kod) {
    case "yikama_donus": return cevir("Returned from Washing");
    case "kumlama_gidis": return cevir("Sent to sand blasting");
    case "kumlama_donus": return cevir("Returned from Sand blasting");
    case "survrep": return cevir("Inspection Document (SURVREP)");
    default: return kod;
  }
}

/** Disposition girdisi okunan satırda MRB bağının cümlesi; "not"ta sebebi sunucunun cümlesi söyler. */
function mrbNotu(m: DispositionMrbSonucu | undefined): SatirGorunumu["notlar"] {
  if (!m) return [];
  if (m.durum === "baglandi") return [{ metin: cevir("MRB {0} matched: RN↔SN link established.", m.sn), ton: "ink-2" }];
  if (m.durum === "mrb_yok") return [{ metin: cevir("No MRB with SN {0} yet. It is linked automatically when that MRB is uploaded.", m.sn), ton: "ink-2" }];
  return [{ metin: cevir(m.not), ton: "warn" }];
}

/**
 * Yalnız PDF satırının sonucu sırada bir yer söylemiyorsa: aynı PDF yeniden gelmiş ve rapor dönüştürücüdedir (`donusturuluyor`),
 * ya da iş yanıt kurulurken sıradan çıkmıştır. "Kuyruğa eklendi" denmez; satır gerçeği söyler.
 */
function siraDisiGorunumu(tur: RaporTuru, s: OrtakYanit, bos: SatirGorunumu): SatirGorunumu | null {
  if (!s.kuyrugaGirdi || s.siraNo) return null;
  if (s.donusturuluyor) return { ...bos, etiketler: [{ metin: cevir("converting"), ton: "info" }], aciklama: { metin: cevir("{0} was already uploaded and is being converted now; nothing was changed.", raporAdi(tur, s.rn)), ton: "ink-2" } };
  return { ...bos, etiketler: [{ metin: cevir("uploaded"), ton: "ok" }], aciklama: { metin: cevir("{0} is uploaded and has no input yet. Its converter state is listed under Reports waiting for their input.", raporAdi(tur, s.rn)), ton: "ink-2" } };
}

/** Hata satırı: iki türde ortak; yalnız reddedilen girdinin cümlesi türe göre. */
function hataGorunumu<D extends DosyaBenzeri>(tur: RaporTuru, satir: RaporSatiri<D>, bos: SatirGorunumu): SatirGorunumu {
  const h = satir.hata;
  const etiketler: SatirGorunumu["etiketler"] = [{ metin: cevir("error"), ton: "bad" }];
  if (!h) return { ...bos, etiketler, aciklama: { metin: cevir("Upload failed"), ton: "bad" } };
  // Reddedilen girdi PDF'le eşliyse PDF de kaydedilmemiştir: satır excel'siz yeniden denenebilir.
  if (h.kaynak === "excel" && satir.pdf) {
    const metin = tur === "disposition"
      ? cevir("Disposition input rejected: {0} The PDF was not saved either.", cevir(h.metin))
      : cevir("Rework input rejected: {0} The PDF was not saved either.", cevir(h.metin));
    return { ...bos, etiketler, aciklama: { metin, ton: "bad" }, dugme: "excelsiz" };
  }
  // "onkosul": riserin Disposition raporu yüklendikten sonra aynı satır yeniden gönderilir.
  return { ...bos, etiketler, aciklama: { metin: cevir(h.metin), ton: "bad" }, dugme: h.tekrarlanabilir || h.kaynak === "onkosul" ? "tekrar" : null };
}

function dispositionGorunumu(s: DispositionYaniti, bos: SatirGorunumu): SatirGorunumu {
  const uyarilar: SatirGorunumu["notlar"] = s.uyarilar.map((u) => ({ metin: cevir(u), ton: "warn" }));
  const adimlar: SatirGorunumu["notlar"] = s.atlananAdimlar?.length ? [{ metin: cevir("Field steps recorded as not performed for {0}: {1}.", s.rn, s.atlananAdimlar.map(adimAdi).join(", ")), ton: "ink-3" }] : [];
  const teslim: SatirGorunumu["notlar"] = s.teslimAtlandi ? [{ metin: cevir("{0} was still waiting on the Receiving list; Receiving is skipped.", s.rn), ton: "ink-3" }] : [];
  const o = s.ozet ?? { gorunurKomponent: 0, kalem: 0, celiski: 0 };
  switch (s.sonuc) {
    case "degismedi":
      return { ...bos, etiketler: [{ metin: cevir("not changed"), ton: "warn" }], aciklama: { metin: cevir("{0} already has a disposition input; nothing from this row was saved.", s.rn), ton: "ink-2" } };
    case "pdf_gerekli":
      return { ...bos, etiketler: [{ metin: cevir("needs its PDF"), ton: "warn" }], aciklama: { metin: cevir("{0} has no Disposition report here, so an excel alone is not sent from this window. Add its PDF, or use Upload disposition input.", s.rn), ton: "ink-2" } };
    case "girdi_baglandi": {
      const cumleler = [
        cevir("The Disposition report of {0} had no disposition input, so this excel was read: {1} components · {2} items · {3} conflicts.", s.rn, o.gorunurKomponent, o.kalem, o.celiski),
        s.siradanCikti ? cevir("The record left the converter queue.") : null,
        s.pdfKaydedilmedi ? cevir("The PDF on the record was kept; the PDF in this row was not saved.") : null,
      ];
      return { ...bos, etiketler: [{ metin: girdiBaglandiAdi("disposition"), ton: "ok" }], aciklama: { metin: cumleler.filter(Boolean).join(" "), ton: "ink-2" }, notlar: [...mrbNotu(s.mrb), ...uyarilar, ...adimlar, ...teslim] };
    }
    case "yuklendi": {
      const siraDisi = siraDisiGorunumu("disposition", s, bos);
      if (siraDisi) return { ...siraDisi, notlar: [...uyarilar, ...adimlar, ...teslim] };
      const metin = s.kuyrugaGirdi
        ? cevir("No disposition input: added to the converter queue ({0}). {1} is on the Field list; its items appear on Inspection verification when the excel arrives.", s.siraNo ? sirali(s.siraNo) : "—", s.rn)
        : cevir("Disposition input read: {0} components · {1} items · {2} conflicts. {3} is on Inspection verification.", o.gorunurKomponent, o.kalem, o.celiski, s.rn);
      return { ...bos, etiketler: [{ metin: cevir("uploaded"), ton: "ok" }], aciklama: { metin, ton: "ink-2" }, notlar: [...(s.kuyrugaGirdi ? [] : mrbNotu(s.mrb)), ...uyarilar, ...adimlar, ...teslim] };
    }
  }
}

function reworkGorunumu(s: ReworkYaniti, bos: SatirGorunumu): SatirGorunumu {
  // İşlemenin notları riserin sayfasında listelenir; satır yalnız sayısını söyler (çoğu satırda vardır, sarı yazılsa hata sanılır).
  // D-069: "n ADDITIONAL REJECT row opens as a Replacement Item in Item level Triage: …" notu riserin sayfasındaki listeye YAZILMAZ (bulgu
  // kalem olarak açılmıştır); bu yüzden sayıya katılmaz ve satırda kendi cümlesiyle, koyu yazılır.
  const ekNotlari = s.uyarilar.filter(ekRejectNotuMu);
  const sayfadaki = s.uyarilar.length - ekNotlari.length;
  const notlar: SatirGorunumu["notlar"] = [
    ...ekNotlari.map((u) => ({ metin: cevir(u), ton: "ink-2" as const })),
    ...(sayfadaki ? [{ metin: cevir("{0} notes are listed on the riser's page.", sayfadaki), ton: "ink-3" as const }] : []),
  ];
  const o = s.ozet ?? { kapanan: 0, devir: 0, dogrulanmayan: 0, ek: 0 };
  switch (s.sonuc) {
    case "degismedi":
      return { ...bos, etiketler: [{ metin: cevir("not changed"), ton: "warn" }], aciklama: { metin: cevir("{0} already has its Rework report processed; nothing from this row was saved.", s.rn), ton: "ink-2" } };
    case "pdf_gerekli":
      return { ...bos, etiketler: [{ metin: cevir("needs its PDF"), ton: "warn" }], aciklama: { metin: cevir("{0} has no Rework report here, so an excel alone is not sent from this window. Add its PDF, or use Upload Rework Scope on the riser's page.", s.rn), ton: "ink-2" } };
    case "girdi_baglandi": {
      const cumleler = [
        cevir("The Rework report of {0} had no rework input, so this excel was processed: {1} closures · {2} carry over · {3} unverified · {4} additional verifications.", s.rn, o.kapanan, o.devir, o.dogrulanmayan, o.ek),
        s.siradanCikti ? cevir("The record left the converter queue.") : null,
        s.pdfKaydedilmedi ? cevir("The PDF on the record was kept; the PDF in this row was not saved.") : null,
      ];
      return { ...bos, etiketler: [{ metin: girdiBaglandiAdi("rework"), ton: "ok" }], aciklama: { metin: cumleler.filter(Boolean).join(" "), ton: "ink-2" }, notlar };
    }
    case "yuklendi": {
      // Yalnız PDF ve riserin disposition girdisi yok: iş sıradadır ama girdi sisteme girene kadar dönüştürücüye verilmez.
      // Rapor şu anda dönüştürücüdeyse (aynı PDF yeniden geldi) satır onu söyler; set o arada sıfırlanmış olsa da "kuyruğa eklendi" denmez.
      if (s.donusturuluyor) {
        const donusturulen = siraDisiGorunumu("rework", s, bos);
        if (donusturulen) return donusturulen;
      }
      if (s.kuyrugaGirdi && s.onKosul) return { ...bos, etiketler: [{ metin: cevir("waiting for disposition"), ton: "warn" }], aciklama: { metin: cevir("No rework input: added to the converter queue. It is converted after the disposition input of {0} is in the system.", s.rn), ton: "ink-2" } };
      const siraDisi = siraDisiGorunumu("rework", s, bos);
      if (siraDisi) return siraDisi;
      const metin = s.kuyrugaGirdi
        ? cevir("No rework input: added to the converter queue ({0}).", s.siraNo ? sirali(s.siraNo) : "—")
        : cevir("Rework input processed: {0} closures · {1} carry over · {2} unverified · {3} additional verifications.", o.kapanan, o.devir, o.dogrulanmayan, o.ek);
      return { ...bos, etiketler: [{ metin: cevir("uploaded"), ton: "ok" }], aciklama: { metin, ton: "ink-2" }, notlar: s.kuyrugaGirdi ? [] : notlar };
    }
  }
}

/** Satırın "Result" hücresi (tasarım: dogrulama--disposition-toplu-yukleme, dogrulama--rework-toplu-yukleme). */
export function satirGorunumu<D extends DosyaBenzeri>(tur: RaporTuru, satir: RaporSatiri<D>): SatirGorunumu {
  const bos: SatirGorunumu = { etiketler: [], aciklama: null, notlar: [], rnSecimi: false, dugme: null };
  switch (satir.durum) {
    case "bekliyor":
      return { ...bos, etiketler: [{ metin: cevir("waiting"), ton: "neutral" }] };
    case "yukleniyor":
      return { ...bos, etiketler: [{ metin: cevir("uploading…"), ton: "info" }] };
    case "rn_gerekli":
      return {
        ...bos,
        etiketler: [{ metin: cevir("riser could not be read"), ton: "warn" }],
        aciklama: { metin: satir.hata ? cevir(satir.hata.metin) : cevir("The file name has no riser number. Select the riser and retry this row."), ton: "ink-2" },
        rnSecimi: true,
      };
    case "hata":
      return hataGorunumu(tur, satir, bos);
    case "tamam": {
      const s = satir.sonuc;
      if (!s) return { ...bos, etiketler: [{ metin: cevir("uploaded"), ton: "ok" }] };
      // Satırın sonucu pencerenin ucundan gelir: tür pencereninkidir.
      return tur === "disposition" ? dispositionGorunumu(s as DispositionYaniti, bos) : reworkGorunumu(s as ReworkYaniti, bos);
    }
  }
}

export interface TopluOzet {
  toplam: number;
  /** Sonucu belli satırlar (sırada ve yüklenmekte olanlar dışındakiler). */
  biten: number;
  yuklendi: number;
  girdiBaglandi: number;
  degismedi: number;
  rnGerekli: number;
  pdfGerekli: number;
  hata: number;
  yukleniyor: number;
  bekliyor: number;
}

/** Pencerenin ilerleme satırı: "9 of 12 done" ve durum sayaçları. Öteki pencerenin dosyası "error" sayılır. */
export function topluOzet<D extends DosyaBenzeri>(satirlar: readonly RaporSatiri<D>[]): TopluOzet {
  const o: TopluOzet = { toplam: satirlar.length, biten: 0, yuklendi: 0, girdiBaglandi: 0, degismedi: 0, rnGerekli: 0, pdfGerekli: 0, hata: 0, yukleniyor: 0, bekliyor: 0 };
  for (const s of satirlar) {
    if (s.durum === "bekliyor") o.bekliyor++;
    else if (s.durum === "yukleniyor") o.yukleniyor++;
    else if (s.durum === "rn_gerekli") o.rnGerekli++;
    else if (s.durum === "hata") o.hata++;
    else if (s.sonuc?.sonuc === "girdi_baglandi") o.girdiBaglandi++;
    else if (s.sonuc?.sonuc === "degismedi") o.degismedi++;
    else if (s.sonuc?.sonuc === "pdf_gerekli") o.pdfGerekli++;
    else o.yuklendi++;
  }
  o.biten = o.toplam - o.bekliyor - o.yukleniyor;
  return o;
}

/* ------------------------------------------- "Reports waiting for their input" ------------------------------------------- */

/** Bekleyen raporun "State" hücresinin okuduğu alanlar (`GET /api/rapor` satırı). */
export interface BekleyenDurumGirdisi {
  hazir: boolean;
  kuyrugaEklenebilir: boolean;
  is: { donusum: "sirada" | "donusturuluyor" | "hata" | null };
}

/**
 * Bekleyen raporun durumu ve düğmesi: `on_kosul` = sırada ama riserin disposition girdisi yok (dönüştürücüye verilmez) ·
 * `sirada` · `donusturuluyor` (düğme yok) · `hata` (+ Retry) · `yok` = işi yok (+ Add to queue, sunuluyorsa).
 * Sıradaki iş her zaman çıkarılabilir: riser girdiyi beklerken tutulmaz.
 */
export function bekleyenDurumu(b: BekleyenDurumGirdisi): { durum: "on_kosul" | "sirada" | "donusturuluyor" | "hata" | "yok"; dugme: "ekle" | "tekrar" | "cikar" | null } {
  switch (b.is.donusum) {
    case "sirada":
      return { durum: b.hazir ? "sirada" : "on_kosul", dugme: "cikar" };
    case "donusturuluyor":
      return { durum: "donusturuluyor", dugme: null };
    case "hata":
      return { durum: "hata", dugme: b.kuyrugaEklenebilir ? "tekrar" : null };
    default:
      return { durum: "yok", dugme: b.kuyrugaEklenebilir ? "ekle" : null };
  }
}
